import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { apiService } from '@/services/api';
import { useUser } from '@/contexts/UserContext';

const SupportContext = createContext();

export const SupportProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const { user } = useUser();

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const result = await apiService.getSupportRequests(user.id);
      const list = Array.isArray(result) ? result : result?.items || [];
      list.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      setTickets(list);
    } catch (err) {
      console.error('Failed to fetch support tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchMessages = useCallback(async (ticketId) => {
    try {
      const result = await apiService.getSupportMessages(ticketId);
      setMessages(Array.isArray(result) ? result : result?.items || []);
    } catch (err) {
      console.error('Failed to fetch support messages:', err);
    }
  }, []);

  const startPolling = useCallback((ticketId) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      fetchMessages(ticketId);
    }, 30000);
  }, [fetchMessages, stopPolling]);

  const openSupport = useCallback(() => {
    setView('list');
    setSelectedTicketId(null);
    setSelectedTicket(null);
    setMessages([]);
    setIsOpen(true);
    fetchTickets();
  }, [fetchTickets]);

  const openNewRequest = useCallback(() => {
    setView('new');
    setSelectedTicketId(null);
    setSelectedTicket(null);
    setMessages([]);
    setIsOpen(true);
  }, []);

  const openThread = useCallback(async (ticket) => {
    const ticketId = typeof ticket === 'object' ? ticket.id : ticket;
    const ticketObj = typeof ticket === 'object' ? ticket : tickets.find(t => t.id === ticket);
    setSelectedTicketId(ticketId);
    setSelectedTicket(ticketObj || null);
    setView('thread');
    setIsOpen(true);
    setLoading(true);
    await fetchMessages(ticketId);
    setLoading(false);
    startPolling(ticketId);
  }, [tickets, fetchMessages, startPolling]);

  const close = useCallback(() => {
    setIsOpen(false);
    stopPolling();
    setTimeout(() => {
      setView('list');
      setSelectedTicketId(null);
      setSelectedTicket(null);
      setMessages([]);
    }, 300);
  }, [stopPolling]);

  const submitRequest = useCallback(async (data) => {
    if (!user?.id) return;
    try {
      setSubmitting(true);
      const result = await apiService.createSupportRequest({
        user_id: user.id,
        organization_id: user.organization_id,
        ...data
      });
      await fetchTickets();
      await openThread(result);
      return result;
    } catch (err) {
      console.error('Failed to create support request:', err);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [user?.id, user?.organization_id, fetchTickets, openThread]);

  const sendMessage = useCallback(async (body, attachments = []) => {
    if (!selectedTicketId) return;
    try {
      setSubmitting(true);
      await apiService.addSupportMessage(selectedTicketId, body, attachments);
      await fetchMessages(selectedTicketId);
    } catch (err) {
      console.error('Failed to send support message:', err);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [selectedTicketId, fetchMessages]);

  const value = useMemo(() => ({
    isOpen,
    view,
    selectedTicketId,
    selectedTicket,
    tickets,
    messages,
    loading,
    submitting,
    openSupport,
    openNewRequest,
    openThread,
    close,
    fetchTickets,
    submitRequest,
    sendMessage,
  }), [
    isOpen, view, selectedTicketId, selectedTicket, tickets, messages, loading, submitting,
    openSupport, openNewRequest, openThread, close, fetchTickets, submitRequest, sendMessage
  ]);

  return (
    <SupportContext.Provider value={value}>
      {children}
    </SupportContext.Provider>
  );
};

export const useSupport = () => {
  const context = useContext(SupportContext);
  if (context === undefined) {
    throw new Error('useSupport must be used within a SupportProvider');
  }
  return context;
};
